import isNil from 'lodash/isNil';
import forEach from 'lodash/forEach';
import { isNumber } from '@/utils/number';

/**
 * Task Class
 * TaskManager에서 관리하는 Task에 대한 class
 * @author Ju
 */
/* eslint-disable no-underscore-dangle */
class Task {
  constructor({ taskName, interval, run, exceptAllStop, repeat, repeatEndCallback }) {
    this.taskName = taskName;
    this.stopped = true;
    this.interval = interval;
    this.exceptAllStop = isNil(exceptAllStop) ? false : exceptAllStop;

    if (!isNil(repeat) && isNumber(repeat)) {
      this.repeat = repeat;
      this.repeatEndCallback = repeatEndCallback;
      this.taskRunCount = 0;
      this.run = () => {
        run();
        this.taskRunCount += 1;
        if (this.repeat <= this.taskRunCount) {
          if (!isNil(this.repeatEndCallback)) {
            this.repeatEndCallback();
          }
          this.destroy();
        }
      };
    } else {
      this.run = run;
    }
  }
  start() {
    if (this.stopped) {
      // this.runner = setInterval(this.run, this.interval);
      // this.stopped = false;
      this.manager._start(this);
    }
  }
  stop(andRemove) {
    this.manager._stop(this, andRemove);
  }
  destroy() {
    this.manager._stop(this, true);
  }
}

/**
 * Task Manager Class
 * reference from ExtJs (TaskRunner)
 * @author Ju
 */
let instance = null;
class TaskManager {
  constructor() {
    if (instance) return instance;
    this.tasks = {};
    instance = this;
  }

  /**
   * Task를 생성한다. (private)
   * @author Ju
   * @param  {Function} fn       [description]
   * @param  {[type]}   interval [description]
   * @return {[type]}            [description]
   */
  _newTask(config) {
    const task = new Task(config);
    task.manager = this;
    this.tasks[config.taskName] = task;
  }

  /**
   * Task를 시작한다. (private)
   * Task Class 자체 메소드로 제공해줘도 되나, TaskManager에서 Task의 모든 기능을 통제하기 위해 TaskManager에서 제공.
   * @author Ju
   * @param  {[type]} task [description]
   * @return {[type]}      [description]
   */
  /* eslint-disable class-methods-use-this */
  _start(task) {
    task.runner = setInterval(task.run, task.interval);
    task.stopped = false;
  }

  _restart(task) {
    task.stop();
    task.taskRunCount = 0;
    task.runner = setInterval(task.run, task.interval);
    task.stopped = false;
  }
  /* eslint-enable class-methods-use-this */

  /**
   * Task를 중단한다. (private)
   * @author Ju
   * @param  {[type]} task      [description]
   * @param  {[type]} andRemove [description]
   * @return {[type]}           [description]
   */
  _stop(task, andRemove) {
    clearInterval(task.runner);
    if (andRemove) {
      delete this.tasks[task.taskName];
    } else {
      task.stopped = true;
    }
  }

  /**
   * Task를 생성한다. 동일한 Task명을 가진 Task가 이미 있을 경우, 해당 Task를 제거하고 새로운 Task를 생성한다.
   *
   * @param {String} taskName Task명
   * @param {Number} interval Task 주기 (seconds)
   * @param {Object} taskFn Task 수행 함수
   * @param {Number} 반복 횟수
   * @param {boolean} exceptAllStop 전체 중지/제거 시 제외 여부
   */
  createTask(taskName, interval, taskFn, exceptAllStop, repeat, repeatEndCallback) {
    if (this.existTask(taskName)) {
      this._stop(this.tasks[taskName], true);
    }

    return this._newTask({
      taskName,
      interval: interval * 1000,
      run: taskFn,
      exceptAllStop,
      repeat,
      repeatEndCallback,
    });
  }

  /**
   * Task명에 해당하는 Task를 제거한다.
   *
   * @param {String} taskName Task명
   */
  removeTask(taskName) {
    if (this.existTask(taskName)) {
      const task = this.tasks[taskName];
      this._stop(task, true);
    }
  }

  /*
   * 모든 Task를 제거한다.
   */
  removeAllTask() {
    forEach(this.tasks, (value, key) => {
      if (!isNil(value) && !value.exceptAllStop) {
        this.removeTask(key);
      }
    });
    // this.tasks = {};
  }

  /**
   * Task명에 해당하는 Task를 실행시킨다.
   *
   * @param {String} taskName Task명
   */
  startTask(taskName) {
    if (this.existTask(taskName)) {
      const task = this.tasks[taskName];
      this._start(task);
      return true;
    }
    return false;
  }

  /**
   * Task명에 해당하는 Task를 다시 실행시킨다.
   * repeat이 설정된 Task는 repeat 횟수가 초기화된다.
   * repeat이 없는 Task는 startTask와 동일하다.
   * @author Ju
   * @param  {[type]} taskName [description]
   * @return {[type]}          [description]
   */
  restartTask(taskName) {
    if (this.existTask(taskName)) {
      const task = this.tasks[taskName];
      this._restart(task);
      return true;
    }
    return false;
  }

  /**
   * Task명에 해당하는 Task를 중지시킨다.
   *
   * @param {String} taskName Task명
   */
  stopTask(taskName) {
    if (this.existTask(taskName)) {
      const task = this.tasks[taskName];
      this._stop(task);
      return true;
    }
    return false;
  }

  /*
   * Task 전체를 중지시킨다.
   */
  stopAllTask() {
    forEach(this.tasks, (value, key) => {
      if (!isNil(value) && !value.exceptAllStop) {
        this.stopTask(key);
      }
    });
  }

  /*
   * Task 전체를 실행시킨다.
   */
  startAllTask() {
    forEach(this.tasks, (value, key) => {
      if (!isNil(value)) {
        this.startTask(key);
      }
    });
  }

  /**
   * Task명에 해당하는 Task가 존재하는지 확인한다.
   *
   * @param {String} taskName Task명
   */
  existTask(taskName) {
    return !isNil(this.tasks[taskName]);
  }

  /*
   * 현재 실행 중인 Task를 반환한다.
   *
   */
  getRunningTasks() {
    const runningTasks = [];
    forEach(this.tasks, (value) => {
      if (!isNil(value) && !value.stopped) {
        runningTasks.push(value);
      }
    });
    return runningTasks;
  }

  /*
   * 현재 실행 중인 Task의 이름들을 반환한다.
   *
   */
  getRunningTaskNames() {
    const runningTaskNames = [];
    forEach(this.tasks, (value, key) => {
      if (!isNil(value) && !value.stopped) {
        runningTaskNames.push(key);
      }
    });

    return runningTaskNames;
  }

  /*
   * 해당 Task가 실행 중인지 확인한다.
   *
   */
  isRunningTask(taskName) {
    if (this.existTask(taskName)) {
      const task = this.tasks[taskName];
      return !task.stopped;
    }
    return null;
  }

  /**
   * Task명에 해당하는 Task를 반환한다.
   *
   * @param {String} taskName Task명
   */
  getTask(taskName) {
    if (this.existTask(taskName)) {
      const task = this.tasks[taskName];
      return task;
    }

    return null;
  }

  /*
   * 현재 Task를 가진 모든 Task명을 반환한다.
   */
  getTaskNames() {
    const taskNames = [];
    forEach(this.tasks, (value, key) => {
      if (!isNil(value)) {
        taskNames.push(key);
      }
    });
    return taskNames;
  }

  /*
   * 모든 Task를 반환한다.
   */
  getTasks() {
    const tasks = [];
    forEach(this.tasks, (value) => {
      if (!isNil(value)) {
        tasks.push(value);
      }
    });
    return tasks;
  }

  /**
 * 해당 Task의 interval를 변경한다.
 * @param  {[type]} taskName [description]
 * @param  {[type]} interval [description]
 * @param  {[type]} restart  [description]
 * @return {[type]}          [description]
 * @author jda
 */
  changeInterval(taskName, interval, restart) {
    if (this.existTask(taskName)) {
      const task = this.tasks[taskName];
      task.interval = interval * 1000;
      if (restart) {
        this._restart(task);
      }
    }
  }

  /**
   * Task명에 해당하는 Task의 interval 설정
   *
   * @param {String} taskName Task명
   */
  // setInterval(taskName, interval) {
  //   if (this.existTask(taskName)) {
  //     const task = this.tasks[taskName];
  //     task.interval = interval;
  //   }
  // }
}
/* eslint-enable no-underscore-dangle */

const taskManager = new TaskManager();
export default taskManager;



// WEBPACK FOOTER //
// ./src/utils/task-manager.js